import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { GraduationCap, Users, School, Building2, BookOpen } from "lucide-react";
import GoogleTrustBadge from "@/components/GoogleTrustBadge";

/**
 * Unified registration.
 *
 * ONE form. ONE Create Account button. Role is picked via attractive cards.
 * Same auth account for everyone; role only decides where we route AFTER
 * the account is created:
 *   student   → /dashboard
 *   partner   → /onboarding/partner (6-step wizard)
 *   parent    → /parent/dashboard
 *   teacher   → /centre/teacher-apply (existing 5-step application)
 *
 * Supports ?role=<student|partner|parent|teacher> to preselect a role
 * (used by /login quick shortcuts and Centre landing CTAs).
 */

const ROLES = [
  { key: "student", label: "Student / Learner", icon: GraduationCap,
    accent: "#DC2626", desc: "Browse courses, take practice tests, earn certificates." },
  { key: "partner", label: "Training Partner", icon: Building2,
    accent: "#F59E0B", desc: "Publish your org's courses with dual-brand certificates." },
  { key: "parent",  label: "Learning Centre / Parent", icon: Users,
    accent: "#064E3B", desc: "Enrol your child in the Teacher4Me Learning Centre." },
  { key: "teacher", label: "Instructor / Tutor", icon: School,
    accent: "#0F766E", desc: "Apply to teach students online or in-centre." },
];

// Post-register redirect target per role
const REDIRECTS = {
  student: "/dashboard",
  partner: "/onboarding/partner",
  parent:  "/parent/dashboard",
  teacher: "/centre/teacher-apply",  // teacher role users go finish the 5-step app
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ["student", "partner", "parent", "teacher"].includes(params.get("role"))
    ? params.get("role")
    : "student";
  const [selected, setSelected] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  // Keep in sync if the query param changes (e.g. user clicks a different shortcut)
  useEffect(() => {
    const r = params.get("role");
    if (r && ["student", "partner", "parent", "teacher"].includes(r)) setSelected(r);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await register(name, email, pw, selected);
      toast.success("Account created — welcome to Teacher4Me!");
      nav(REDIRECTS[selected] || "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] px-6 py-16" data-testid="register-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="label-eyebrow text-[#DC2626]">Create your account</div>
          <h1 className="font-display font-black text-4xl mt-2">Join Teacher4Me</h1>
          <p className="text-[#4B5563] mt-2">One login for every role — pick what fits you today, add more later.</p>
        </div>

        <div className="mb-8"><GoogleTrustBadge variant="light" /></div>

        <form onSubmit={submit} className="grid md:grid-cols-2 gap-6 items-start">
          {/* ---------------- Left on desktop, SECOND on mobile: standard fields ---------------- */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 space-y-4 order-2 md:order-1">
            <div>
              <label className="text-sm font-semibold">Full name</label>
              <input required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
            </div>
            <div>
              <label className="text-sm font-semibold">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
            </div>
            <div>
              <label className="text-sm font-semibold">Password</label>
              <input required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="register-password"
                className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#D92D20]" />
              <p className="text-xs text-[#4B5563] mt-1">At least 6 characters.</p>
            </div>
            <button type="submit" disabled={busy}
              data-testid="register-submit"
              className="btn-primary w-full mt-2">
              {busy ? "Creating account…" : "Create account"}
            </button>
            <p className="text-xs text-center text-[#4B5563]">
              Already have an account?{" "}
              <Link to="/login" className="text-[#D92D20] font-semibold" data-testid="link-login">Sign in</Link>
            </p>
          </div>

          {/* ---------------- Right on desktop, FIRST on mobile: role picker ---------------- */}
          <div className="order-1 md:order-2">
            <div className="text-sm font-semibold mb-3 flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#D92D20] text-white text-[10px] font-black">1</span>
              I want to join Teacher4Me as:
            </div>
            <div className="grid grid-cols-1 gap-3">
              {ROLES.map((r) => {
                const Icon = r.icon;
                const active = selected === r.key;
                return (
                  <button type="button"
                    key={r.key}
                    data-testid={`register-role-${r.key}`}
                    onClick={() => setSelected(r.key)}
                    className={`text-left flex items-start gap-3 border-2 rounded-xl p-4 transition-all ${
                      active
                        ? "border-[#D92D20] bg-white shadow-sm"
                        : "border-[#E5E7EB] bg-white hover:border-[#D92D20]/50"
                    }`}
                    aria-pressed={active}>
                    <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0"
                      style={{ background: r.accent + "1A" }}>
                      <Icon className="w-5 h-5" style={{ color: r.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-sm">{r.label}</div>
                        {active && (
                          <span className="text-[10px] font-bold text-white bg-[#D92D20] px-2 py-0.5 rounded-full">SELECTED</span>
                        )}
                      </div>
                      <div className="text-xs text-[#4B5563] mt-0.5 line-clamp-2">{r.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-[#6B7280] mt-3">
              <BookOpen className="w-3 h-3 inline mr-1 -mt-0.5" />
              Same email + password powers every dashboard. Multi-role support built in.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
